<script setup lang="ts">
import { VIcon } from '@/modules/shared/components'
import { Subscription } from '../types'

const props = withDefaults(
  defineProps<{
    subscription: Subscription
    isRecommended?: boolean
    isSelected?: boolean
    disabled?: boolean
    selectable?: boolean
    scaleOnSelect?: boolean
    onClick?: (value: Subscription) => void
  }>(),
  {
    selectable: true,
    scaleOnSelect: true,
  },
)

const handleClick = () => {
  if (props.disabled || !props.selectable) return
  props.onClick?.(props.subscription)
}
</script>

<template>
  <button
    @click="handleClick"
    type="button"
    class="group relative flex h-full w-full transform flex-col justify-between"
    :class="{
      'cursor-not-allowed opacity-60 shadow-none hover:shadow-none': disabled,
      'cursor-default': !selectable && !disabled,
    }"
    :disabled="disabled"
    :aria-pressed="isSelected"
    :aria-disabled="disabled || !selectable"
  >
    <div
      class="flex h-full flex-col space-y-0.5 rounded-[10px] transition-transform duration-200"
      :class="[
        isRecommended ? 'bg-[#1f4f6a]' : 'bg-gray-200',
        isSelected ? 'shadow-lg ' : '',
        isSelected && scaleOnSelect ? 'scale-[1.03]' : '',
      ]"
    >
      <div v-if="isRecommended" class="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase text-white">
        <span>Recommended</span>
      </div>

      <div
        class="rounded-[10px] p-0.5"
        :class="isSelected ? 'bg-[#3b88af]' : isRecommended ? 'bg-[#1f4f6a]' : 'bg-gray-200'"
      >
        <div
          class="flex min-h-[270px] flex-1 flex-col rounded-lg bg-white px-4 py-6 text-left text-gray-900 transition-colors duration-200"
        >
          <div
            v-if="isSelected"
            class="absolute -bottom-2.5 left-1/2 flex -translate-x-1/2 transform items-center gap-1 rounded-full bg-[#3b88af] px-2 py-1 text-[11px] font-semibold uppercase text-white"
          >
            <VIcon name="Check" class="h-4 w-4" />
            <span>Selected</span>
          </div>

          <div class="flex flex-1 flex-col">
            <div class="flex flex-wrap items-center justify-between">
              <div class="text-left text-lg font-medium text-gray-900">{{ subscription.title }}</div>
              <div class="text-xl font-semibold" :class="isSelected ? 'text-[#1f4f6a]' : 'text-gray-900'">
                {{ subscription.price }}
              </div>
            </div>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="label in subscription.included" :key="label" class="flex items-start gap-2">
                <span class="mt-0.5 flex flex-shrink-0 items-center">
                  <VIcon name="Check" class="h-4 w-4 text-[#1f4f6a]" />
                </span>
                <span>{{ label }}</span>
              </li>
              <li v-for="label in subscription.excluded" :key="label" class="flex items-start gap-2">
                <span class="flex flex-shrink-0 items-center">
                  <VIcon name="X" class="mt-0.5 h-4 w-4 text-red-500" />
                </span>
                <span>{{ label }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </button>
</template>
